/**
 * NodeVisualization - Renders a visualization component for a node
 *
 * Handles the connection between a React visualization component and the
 * video registry where renderers are registered. Supports both:
 * - Primitive nodes: renderer is at instanceId.nodeId
 * - Subgraph nodes with vizNode: renderer is at the internal node's path
 *
 * The vizNode feature allows subgraphs to delegate their visualization
 * to an internal node (e.g., a camera inside the subgraph).
 *
 * Data props pattern:
 * - If getProps is provided, it's called with VizDataContext to extract
 *   data props which are spread to the visualization component
 * - This separates data extraction from rendering, improving reusability
 */

import { CompactErrorBoundary } from "./CompactErrorBoundary"
import { useEffect, useRef, useMemo } from "react"

import { Resizable } from "@/ui/components/Resizable"
import { useVideoRenderer } from "./useVideoRenderer"
import type { VizPropsSelector, VizDataContext } from "./VisualisationProps"

export const DEFAULT_VIZ_HEIGHT = 120;



interface NodeVisualizationProps {
  instanceId: string;
  nodeId: string;
  Viz: React.ComponentType<any>;
  getProps?: VizPropsSelector<any>;
  width: number;
  setHeight: React.Dispatch<React.SetStateAction<number>>;
}

export const NodeVisualization = ({ instanceId, nodeId, Viz, getProps, width, setHeight }: NodeVisualizationProps) => {
  const vizRef = useRef<HTMLDivElement>(null);
  const {
    getValue,
    getPayload,
    setCanvas,
    setSize,
    render,
    setValue,
    getFrequencyBins
  } = useVideoRenderer(instanceId, nodeId);

  // Build data context for getProps selector
  const dataContext = useMemo<VizDataContext>(() => ({
    getValue,
    getPayload
  }), [getValue, getPayload]);

  // Extract data props using selector (memoized on context changes)
  const dataProps = useMemo(() => {
    if (!getProps) return {};
    return getProps(dataContext);
  }, [getProps, dataContext]);

  // Watch visualization container for size changes using ResizeObserver
  useEffect(() => {
    const element = vizRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        setHeight(prev => prev !== newHeight ? newHeight : prev);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [setHeight]);

  return (
    <CompactErrorBoundary name={`${nodeId}:viz`}>
      <div ref={vizRef} className="nodrag nopan nowheel">
        <Resizable
          nodeId={nodeId}
          defaultWidth={width}
          defaultHeight={DEFAULT_VIZ_HEIGHT}
          minHeight={80}
          maxHeight={1200}
          maxWidth={1200}
        >
          {(resizedWidth, resizedHeight) => (
            <Viz
              // Base props (always passed)
              height={resizedHeight}
              width={resizedWidth}
              instanceId={instanceId}
              nodeId={nodeId}
              setCanvas={setCanvas}
              setSize={setSize}
              render={render}
              setValue={setValue}
              getFrequencyBins={getFrequencyBins}
              // Data props from selector (if provided)
              {...dataProps}
              // Legacy props for backward compatibility (deprecated)
              getValue={getValue}
              getPayload={getPayload}
            />
          )}
        </Resizable>
      </div>
    </CompactErrorBoundary>
  );
};
